import { notification } from "antd"
import { green } from "@ant-design/colors"
import ThemableButton from "../../Items/ThemableButton"
import { useState } from "react"
import { faCirclePlus } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import CreateInvoiceModal from "./CreateInvoiceModal"
import { useInvoiceSummariesForJobQuery, useTransactionsForJobQuery } from "../../../domain/invoicing/invoicingQueries"
import { WorkareaGroupWithWorkareas } from "../../../domain/workareas/group"
import { WorkareaSummary } from "../../Items/itemSummaryModel"
import { CompletedWorkareaVisit, CreateInvoiceResponse, isUnresolvableTaxRateError } from "../../../domain/invoicing/invoicingApi"
import { buildWorkareaGroupInvoiceTotals, calculateInvoiceTotals } from "../Totals/workareaTotalsModel"
import { InvoicingSettings } from "../../../domain/invoicing/settings/invoicingSettings"
import CreationConfirmationModal from "./CreationConfirmationModal"

type Props = {
  onInvoiceCreated: () => void
  jobNeedsToBeSaved: boolean
  jobId: number
  workareaGroups: WorkareaGroupWithWorkareas[]
  workareaSummaries: WorkareaSummary[]
  completedWorkareaVisits: CompletedWorkareaVisit[]
  hasQboLinkingWarnings: boolean
  invoicingSettings: InvoicingSettings
}

const CreateInvoiceButtonAndModal = ({onInvoiceCreated, jobNeedsToBeSaved, jobId, workareaGroups, workareaSummaries, completedWorkareaVisits, hasQboLinkingWarnings, invoicingSettings}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [exportConfirmationData, setExportConfirmationData] = useState<CreateInvoiceResponse | null>(null)

  const transactionsQuery = useTransactionsForJobQuery(jobId)
  const invoiceSummariesQuery = useInvoiceSummariesForJobQuery(jobId)
  const groupTotals = buildWorkareaGroupInvoiceTotals(workareaGroups, workareaSummaries, completedWorkareaVisits, transactionsQuery.data ?? [])
  const invoiceTotals = calculateInvoiceTotals(groupTotals, invoiceSummariesQuery.data ?? [])
  const disabled = invoiceTotals.notInvoiced <= 0 || hasQboLinkingWarnings
  
  return <>
    <ThemableButton 
      colorPrimary={green.primary} 
      type="primary"
      size="large"
      onClick={() => {
        if(jobNeedsToBeSaved) {
          notification.info({ message: "The job has unsaved changes. Please save before creating an invoice."})
          return 
        }
        setIsOpen(true)
      }}
      icon={<FontAwesomeIcon icon={faCirclePlus} />}
      disabled={disabled}
    >
        Create New Invoice
    </ThemableButton>
    {isOpen && <CreateInvoiceModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      invoicingSettings={invoicingSettings}
      onInvoiceCreated={(data) => {
        setIsOpen(false)
        onInvoiceCreated()
        setExportConfirmationData(data)
      }}
      onInvoiceCreationError={(error) => {
        if (isUnresolvableTaxRateError(error)) {
          return
        }
        setIsOpen(false)
        onInvoiceCreated()
      }}
    />}
    <CreationConfirmationModal
      isOpen={exportConfirmationData !== null}
      onClose={() => setExportConfirmationData(null)}
      data={exportConfirmationData}
    />
  </>
}

export default CreateInvoiceButtonAndModal
